/**
 * List manipulation utilities
 */

/**
 * Trim string values
 */
const trimValue = (value: unknown): unknown => {
    return typeof value === 'string' ? value.trim() : value;
};

/**
 * Create a sort function for arrays of objects
 */
export function sortList<T>(
    primaryKey: keyof T,
    secondaryKey?: keyof T,
    order: 'asc' | 'desc' = 'asc',
): (a: T, b: T) => number {
    const multiplier = order === 'asc' ? 1 : -1;

    return (a: T, b: T): number => {
        // Handle undefined values
        if (a[primaryKey] === undefined || b[primaryKey] === undefined) {
            return 0;
        }

        const primaryA = trimValue(a[primaryKey]) as string | number;
        const primaryB = trimValue(b[primaryKey]) as string | number;

        // Compare primary key
        if (primaryA < primaryB) {
            return -1 * multiplier;
        }
        if (primaryA > primaryB) {
            return 1 * multiplier;
        }

        // If primary keys are equal, compare secondary key
        if (secondaryKey) {
            const secondaryA = trimValue(a[secondaryKey]) as string | number;
            const secondaryB = trimValue(b[secondaryKey]) as string | number;

            if (secondaryA < secondaryB) {
                return -1 * multiplier;
            }
            if (secondaryA > secondaryB) {
                return 1 * multiplier;
            }
        }

        return 0;
    };
}

/**
 * Remove duplicate objects from an array based on a key
 */
export function distinct<T>(array: Array<T>, key: keyof T): Array<T> {
    const seen = new Set();
    return array.filter((item) => {
        const value = item[key];
        if (seen.has(value)) {
            return false;
        }
        seen.add(value);
        return true;
    });
}
